import { Controller } from "@hotwired/stimulus"

// AB-048 flawless-sync: belt-and-suspenders polling fallback.
//
// Broadcasts via ActionCable are the primary delivery mechanism (instant),
// but they can drop if AnyCable isn't running or the subscription lost a
// message during reconnect. This controller polls the same data every 10s
// while the page shows a sync-in-progress state — if broadcasts don't arrive,
// the poll catches up within 10s.
//
// Attach to the sync_progress frame (and/or profile_status) with:
//   data-controller="meta-sync-poller"
//   data-meta-sync-poller-active-value="<%= meta_connect&.update_in_progress? %>"
//   data-meta-sync-poller-interval-value="10000"
//
// Polling stops automatically when the frame is replaced with a version
// that has data-meta-sync-poller-active-value="false" (Stimulus lifecycle).
export default class extends Controller {
  static values = {
    active: { type: Boolean, default: false },
    interval: { type: Number, default: 10000 },
    url: { type: String, default: "/meta/connect" },
  }

  connect() {
    // Do NOT _startPolling here — Stimulus fires activeValueChanged on
    // init with the current value and handles start/stop. Calling
    // _startPolling here too would cause a redundant stop-then-start
    // loop on every frame replacement.
    console.log(`[meta-sync-poller] connect active=${this.activeValue} interval=${this.intervalValue}ms`)
  }

  disconnect() {
    if (this._timer) console.log(`[meta-sync-poller] disconnect — stopping`)
    this._stopPolling()
  }

  activeValueChanged(newValue, prevValue) {
    // Stimulus fires this on init with both values equal — skip to avoid noise
    if (newValue === prevValue) {
      if (newValue) this._startPolling()
      return
    }
    console.log(`[meta-sync-poller] active ${prevValue} → ${newValue}`)
    if (newValue) {
      this._startPolling()
    } else {
      this._stopPolling()
    }
  }

  _startPolling() {
    this._stopPolling()
    this._timer = setInterval(() => this._poll(), this.intervalValue)
    console.log(`[meta-sync-poller] started polling every ${this.intervalValue}ms`)
  }

  _stopPolling() {
    if (this._timer) {
      clearInterval(this._timer)
      this._timer = null
      console.log(`[meta-sync-poller] stopped polling`)
    }
  }

  async _poll() {
    console.log(`[meta-sync-poller] polling ${this.urlValue}`)
    try {
      const response = await fetch(this.urlValue, {
        headers: { "Accept": "text/vnd.turbo-stream.html" },
        credentials: "same-origin",
      })
      console.log(`[meta-sync-poller] response: ${response.status}`)
      if (!response.ok) return

      const contentType = response.headers.get("Content-Type") || ""
      if (!contentType.includes("turbo-stream")) {
        console.warn(`[meta-sync-poller] unexpected content-type: ${contentType}`)
        return
      }

      const html = await response.text()
      // Use Turbo's native stream processor to apply the replacements
      if (window.Turbo && window.Turbo.renderStreamMessage) {
        window.Turbo.renderStreamMessage(html)
        console.log(`[meta-sync-poller] applied ${html.length} bytes of stream`)
      } else {
        console.error(`[meta-sync-poller] Turbo.renderStreamMessage unavailable`)
      }
    } catch (err) {
      console.error(`[meta-sync-poller] poll failed:`, err)
    }
  }
}
